import {
  LiveAnswersClient,
  LiveAnswersMessage,
} from '@stripe-internal/conversations-client';

const STREAMING_POLL_INTERVAL_MS = 1000;

export interface LiveAnswersPollerState {
  isCancelled: boolean;
  isComplete: boolean;
  error: string | null;
  message: LiveAnswersMessage;
}

/**
 * Polls the LiveAnswers API for message updates at regular intervals.
 * Tracks message state and notifies subscribers of changes until the message
 * is complete, canceled, or encounters an error.
 */
export class LiveAnswersPoller {
  private _client: LiveAnswersClient;

  private _message: LiveAnswersMessage;

  private _state: LiveAnswersPollerState;

  private _listeners = new Set<() => void>();

  private _polling = false;

  private _abortController: AbortController | undefined;

  private _timeout: ReturnType<typeof setTimeout> | undefined;

  private _alreadyCompleted: boolean;

  constructor(client: LiveAnswersClient, message: LiveAnswersMessage) {
    this._client = client;
    this._message = message;
    const isComplete =
      !!message.data?.is_complete ||
      !!message.data?.was_halted ||
      !!message.data?.not_answerable;
    this._alreadyCompleted = isComplete;
    this._state = {
      isCancelled: !!message.data?.was_halted,
      isComplete,
      error: null,
      message,
    };
  }

  public getState(): LiveAnswersPollerState {
    return this._state;
  }

  public subscribe(callback: () => void): () => void {
    this._listeners.add(callback);
    return () => {
      this._listeners.delete(callback);
    };
  }

  public startPolling() {
    if (this._state.isComplete || this._alreadyCompleted) {
      return;
    }
    this._poll();
  }

  public stopPolling() {
    this._abortController?.abort();
    this._polling = false;
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
    }
  }

  private setState(newState: Partial<LiveAnswersPollerState>) {
    this._state = {
      ...this._state,
      ...newState,
    };
    this._listeners.forEach((callback) => callback());
  }

  private async _poll() {
    if (
      this._polling ||
      this._state.message.data?.is_complete ||
      this._state.message.data?.was_halted ||
      this._state.message.data?.not_answerable
    ) {
      return;
    }

    this._polling = true;

    try {
      this._abortController = new AbortController();
      const abortSignal = this._abortController.signal;

      const resp = await this._client.fetchMessage(this._message, {
        abortSignal,
      });

      this._polling = false;

      if (abortSignal.aborted) {
        return;
      }

      if (resp.code === 200 && resp.data?.type === 'success') {
        const {data: respData} = resp;
        const {message: respMessage} = respData;

        const nextState: LiveAnswersPollerState = {
          ...this._state,
          message: respMessage,
        };

        if (
          respMessage.data.is_complete ||
          respMessage.data.was_halted ||
          respMessage.data.not_answerable
        ) {
          if (respMessage.data.was_halted) {
            nextState.isCancelled = true;
          }
          nextState.isComplete = true;
        } else {
          this._timeout = setTimeout(
            () => this._poll(),
            STREAMING_POLL_INTERVAL_MS,
          );
        }

        this.setState(nextState);
      } else {
        let errorMessage =
          'An error occurred while fetching the live answers message.';
        if (resp.data?.type === 'error' && resp.data.error) {
          errorMessage = resp.data.error;
        } else if (resp.error) {
          errorMessage = resp.error;
        }
        this.setState({
          isComplete: true,
          error: errorMessage,
        });
      }
    } catch (e) {
      this._polling = false;
      const errorMessage = e instanceof Error ? e.message : String(e);
      this.setState({
        isComplete: true,
        error: errorMessage,
      });
    }
  }

  public destroy() {
    this.stopPolling();
    this._listeners.clear();
  }
}
